// BiscuitWardrobe — the equipped-outfit overlay layered over the Biscuit image.
// Pure emoji/CSS, positioned absolutely inside Biscuit's (already relative) image
// container. Reused by BiscuitMini and BiscuitFull so the render logic stays in one place.

import { CATALOG_BY_ID } from "@/lib/wardrobe/catalog"
import type { Equipped } from "@/lib/wardrobe/wardrobe-store"

type Size = "mini" | "full"

const POS: Record<Size, { body: string; hat: string; neck: string; accessory: string; effectL: string; effectR: string }> = {
  mini: {
    hat: "-top-1 text-2xl",
    accessory: "top-[34%] text-base",
    neck: "bottom-1 text-lg",
    body: "bottom-0 text-2xl",
    effectL: "top-1 left-0 text-sm",
    effectR: "top-2 right-0 text-sm",
  },
  full: {
    hat: "-top-2 text-5xl",
    accessory: "top-[38%] text-2xl",
    neck: "bottom-2 text-3xl",
    body: "bottom-0 text-5xl",
    effectL: "top-2 left-1 text-2xl",
    effectR: "top-6 right-1 text-2xl",
  },
}

export function BiscuitWardrobe({ equipped, size = "mini" }: { equipped: Equipped; size?: Size }) {
  // A full "look" replaces the corgi image entirely (handled by BiscuitFull/Mini),
  // so the emoji overlays step aside while one is worn.
  if (equipped.look) return null
  const p = POS[size]
  const body = equipped.body ? CATALOG_BY_ID[equipped.body] : null
  const hat = equipped.hat ? CATALOG_BY_ID[equipped.hat] : null
  const neck = equipped.neck ? CATALOG_BY_ID[equipped.neck] : null
  const accessory = equipped.accessory ? CATALOG_BY_ID[equipped.accessory] : null
  const effect = equipped.effect ? CATALOG_BY_ID[equipped.effect] : null

  if (!body && !hat && !neck && !accessory && !effect) return null

  return (
    <>
      {/* Aura/effect: a pair of gently floating emoji flanking Biscuit. */}
      {effect && (
        <>
          <span aria-hidden="true" className={`pointer-events-none absolute drop-shadow animate-float motion-reduce:animate-none ${p.effectL}`}>
            {effect.emoji}
          </span>
          <span aria-hidden="true" className={`pointer-events-none absolute drop-shadow animate-float motion-reduce:animate-none [animation-delay:600ms] ${p.effectR}`}>
            {effect.emoji}
          </span>
        </>
      )}
      {/* Outfit sits over the torso, behind the face/neck/hat layers. */}
      {body && (
        <span aria-hidden="true" className={`pointer-events-none absolute left-1/2 -translate-x-1/2 drop-shadow ${p.body}`}>
          {body.emoji}
        </span>
      )}
      {accessory && (
        <span aria-hidden="true" className={`pointer-events-none absolute left-1/2 -translate-x-1/2 drop-shadow ${p.accessory}`}>
          {accessory.emoji}
        </span>
      )}
      {neck && (
        <span aria-hidden="true" className={`pointer-events-none absolute left-1/2 -translate-x-1/2 drop-shadow ${p.neck}`}>
          {neck.emoji}
        </span>
      )}
      {hat && (
        <span aria-hidden="true" className={`pointer-events-none absolute left-1/2 -translate-x-1/2 drop-shadow ${p.hat}`}>
          {hat.emoji}
        </span>
      )}
    </>
  )
}
